import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import { DragHandle } from "./KanbanBoard.web";
import {
  ANSWER_TYPE_LABELS,
  BankQuestion,
  BankScreen,
  FLOW_LABELS,
  QUESTION_TEXT_LIMIT,
} from "./questionBank";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(1.5),
  },
  header: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  handle: {
    color: theme.palette.text.secondary,
    cursor: "grab",
    display: "inline-flex",
  },
  lock: {
    color: theme.palette.text.secondary,
    display: "inline-flex",
  },
  badge: {
    color: theme.palette.text.secondary,
  },
  helpText: {
    color: theme.palette.text.secondary,
    display: "block",
    marginTop: theme.spacing(0.5),
  },
  answers: {
    margin: theme.spacing(1, 0, 0),
    paddingLeft: theme.spacing(2),
  },
  answerTags: {
    color: theme.palette.text.secondary,
    display: "block",
  },
  toggleAnswers: {
    marginTop: theme.spacing(0.5),
    padding: 0,
    textTransform: "none",
  },
  editActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  actions: {
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
  },
  categorySelect: {
    marginTop: theme.spacing(1),
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
}));

interface Props {
  question: BankQuestion;
  screens: BankScreen[];
  inBank: boolean;
  busy: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  dragHandle: DragHandle;
  onSaveText: (text: string) => void;
  onMoveToScreen: (screen: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onSendToBank: () => void;
  onAddFromBank: (screen: string) => void;
  onRemove: () => void;
}

const QuestionBankCard = ({
  question,
  screens,
  inBank,
  busy,
  canMoveUp,
  canMoveDown,
  dragHandle,
  onSaveText,
  onMoveToScreen,
  onMoveUp,
  onMoveDown,
  onSendToBank,
  onAddFromBank,
  onRemove,
}: Props) => {
  const classes = useStyles();
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(question.text);
  const [showAnswers, setShowAnswers] = useState<boolean>(false);
  const [targetScreen, setTargetScreen] = useState<string>(question.screen);

  const trimmedDraft = draft.trim();
  const canSave = !busy && trimmedDraft !== "" && trimmedDraft !== question.text;

  const startEditing = () => {
    setDraft(question.text);
    setEditing(true);
  };

  const saveText = () => {
    if (!canSave) {
      return;
    }
    onSaveText(trimmedDraft);
    setEditing(false);
  };

  return (
    <Paper
      className={classes.card}
      data-test-id={`bank-card-${question.key}`}
      elevation={0}
    >
      <Box className={classes.header}>
        {dragHandle && (
          <span
            aria-label="Drag to move"
            className={classes.handle}
            data-test-id="drag-handle"
            {...dragHandle}
          >
            <DragIndicatorIcon fontSize="small" />
          </span>
        )}
        {!question.editable && (
          <Tooltip title="Required by the Magic Link pricing logic">
            <span className={classes.lock} data-test-id="locked-question">
              <LockOutlinedIcon fontSize="small" />
            </span>
          </Tooltip>
        )}
        <Chip label={FLOW_LABELS[question.flow]} size="small" />
        <Typography className={classes.badge} variant="caption">
          {ANSWER_TYPE_LABELS[question.answer_type]}
        </Typography>
        {question.editable ? (
          <Typography className={classes.badge} variant="caption">
            Your question
          </Typography>
        ) : (
          <Typography className={classes.badge} variant="caption">
            Required
          </Typography>
        )}
      </Box>

      {editing ? (
        <>
          <TextField
            autoFocus
            data-test-id="question-text-input"
            fullWidth
            inputProps={{ maxLength: QUESTION_TEXT_LIMIT }}
            multiline
            onChange={(event) => setDraft(event.target.value)}
            size="small"
            value={draft}
            variant="outlined"
          />
          <Box className={classes.editActions}>
            <Button
              color="primary"
              data-test-id="save-question-text"
              disabled={!canSave}
              onClick={saveText}
              size="small"
              variant="contained"
            >
              Save
            </Button>
            <Button disabled={busy} onClick={() => setEditing(false)} size="small">
              Cancel
            </Button>
          </Box>
        </>
      ) : (
        <Typography variant="body2">{question.text}</Typography>
      )}

      {question.help_text && (
        <Typography className={classes.helpText} variant="caption">
          {question.help_text}
        </Typography>
      )}

      {question.options.length > 0 && (
        <>
          <Button
            className={classes.toggleAnswers}
            color="primary"
            onClick={() => setShowAnswers(!showAnswers)}
            size="small"
          >
            {showAnswers ? "Hide" : "Show"} {question.options.length} answers
          </Button>
          {showAnswers && (
            <ul className={classes.answers}>
              {question.options.map((option) => {
                const labels = [...(option.tags || []), ...(option.bumps || [])];
                return (
                  <li key={option.text}>
                    <Typography variant="caption">{option.text}</Typography>
                    {labels.length > 0 && (
                      <Typography className={classes.answerTags} variant="caption">
                        {labels.join(" · ")}
                      </Typography>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {question.editable && !editing && (
        <>
          <Box className={classes.actions}>
            <Tooltip title="Edit text">
              <span>
                <IconButton disabled={busy} onClick={startEditing} size="small">
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {!inBank && (
              <>
                <Tooltip title="Move up">
                  <span>
                    <IconButton
                      disabled={busy || !canMoveUp}
                      onClick={onMoveUp}
                      size="small"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      disabled={busy || !canMoveDown}
                      onClick={onMoveDown}
                      size="small"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move to the Question Bank">
                  <span>
                    <IconButton
                      data-test-id="send-to-bank"
                      disabled={busy}
                      onClick={onSendToBank}
                      size="small"
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <Tooltip title="Delete question">
              <span>
                <IconButton
                  data-test-id="remove-question"
                  disabled={busy}
                  onClick={onRemove}
                  size="small"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <TextField
            className={classes.categorySelect}
            disabled={busy}
            fullWidth
            label="Category"
            onChange={(event) => {
              const screen = event.target.value as string;
              if (inBank) {
                setTargetScreen(screen);
              } else {
                onMoveToScreen(screen);
              }
            }}
            select
            size="small"
            value={inBank ? targetScreen : question.screen}
            variant="outlined"
          >
            {screens.map((screen) => (
              <MenuItem key={screen.key} value={screen.key}>
                {screen.title}
              </MenuItem>
            ))}
          </TextField>

          {inBank && (
            <Button
              className={classes.addButton}
              color="primary"
              data-test-id="add-from-bank"
              disabled={busy}
              fullWidth
              onClick={() => onAddFromBank(targetScreen)}
              size="small"
              variant="contained"
            >
              Add to Magic Link
            </Button>
          )}
        </>
      )}
    </Paper>
  );
};

export default QuestionBankCard;
